import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Layers, Loader2, Save, CheckCircle2, AlertOctagon, History, Eye } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StylerMasters() {
  const [masters, setMasters] = useState(null);
  const [editing, setEditing] = useState(null); // type
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [viewing, setViewing] = useState(null); // {type, version, json}

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/template-styler/masters`);
      setMasters(r.data.masters);
    } catch (_) { toast.error("Couldn't load masters"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = async (m) => {
    setEditing(m.type); setErrors([]); setNote("");
    try {
      const r = await axios.get(`${API}/template-styler/masters/${m.type}/versions/${m.active_version}`);
      setText(JSON.stringify(r.data.json, null, 2));
    } catch (_) { setText(""); }
  };

  const validateLocal = () => {
    try { JSON.parse(text); setErrors([]); toast.success("JSON parses"); return true; }
    catch (e) { setErrors([`JSON does not parse: ${e.message}`]); return false; }
  };

  const save = async (activate) => {
    if (!validateLocal()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/template-styler/masters/${editing}/versions`, { json: text, note, activate });
      toast.success(`Saved as version ${r.data.version}${activate ? " (active)" : ""}`);
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.errors) setErrors(d.errors);
      else setErrors([typeof d === "string" ? d : "Save failed"]);
    } finally { setSaving(false); }
  };

  const setActive = async (type, version) => {
    try {
      await axios.put(`${API}/template-styler/masters/${type}/active`, { version });
      toast.success(`Version ${version} is now active`);
      load();
    } catch (_) { toast.error("Couldn't change active version"); }
  };

  const view = async (type, version) => {
    try {
      const r = await axios.get(`${API}/template-styler/masters/${type}/versions/${version}`);
      setViewing(r.data);
    } catch (_) { toast.error("Couldn't load version"); }
  };

  if (!masters) return <div className="ts-loading"><Loader2 className="animate-spin" size={18} /> Loading masters…</div>;

  return (
    <section className="ts-panel" data-testid="styler-masters">
      <div className="ts-section-head"><span><Layers size={15} /> Master templates</span><small>Known-good Creatomate JSON — the source of truth. Paste improved JSON to save a new version; previous versions are kept.</small></div>
      <div className="ts-masters">
        {masters.map((m) => (
          <div key={m.type} className="ts-master-card" data-testid={`styler-master-${m.type}`}>
            <div className="ts-master-head">
              <h4>{m.name}</h4>
              <span className="ts-pill">{m.active_version ? `v${m.active_version} active` : "no master"}</span>
            </div>
            <div className="ts-master-brand">
              {["primary", "accent", "neutral"].map((r) => m.master_brand?.[r] && <i key={r} style={{ background: m.master_brand[r] }} title={`${r} ${m.master_brand[r]}`} />)}
              <span>{m.master_brand?.hero_font} / {m.master_brand?.support_font}</span>
            </div>
            <div className="ts-versions">
              {m.versions.map((v) => (
                <div key={v.version} className={`ts-version ${v.version === m.active_version ? "active" : ""}`} data-testid={`styler-version-${m.type}-${v.version}`}>
                  <History size={12} />
                  <span className="ts-version-no">v{v.version}</span>
                  <span className="ts-version-note">{v.note || "—"}</span>
                  <span className="ts-version-meta">{v.element_count} el · {v.duration}s · {v.created_by}</span>
                  <button className="ts-icon-btn" title="View JSON" onClick={() => view(m.type, v.version)} data-testid={`styler-view-${m.type}-${v.version}`}><Eye size={12} /></button>
                  {v.version !== m.active_version && (
                    <button className="ts-link" onClick={() => setActive(m.type, v.version)} data-testid={`styler-activate-${m.type}-${v.version}`}>Make active</button>
                  )}
                </div>
              ))}
            </div>
            <button className="ts-btn ghost sm" onClick={() => openEditor(m)} data-testid={`styler-edit-${m.type}`}>Edit JSON</button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="ts-editor" data-testid="styler-master-editor">
          <div className="ts-editor-head">
            <h4>New version — {masters.find((m) => m.type === editing)?.name}</h4>
            <input className="ts-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Version note (e.g. tightened price crunch timing)" data-testid="styler-version-note" />
          </div>
          <textarea className="ts-json-edit" value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} data-testid="styler-master-json" />
          {errors.length > 0 && (
            <div className="ts-invalid" data-testid="styler-master-errors">
              <AlertOctagon size={16} />
              <div><strong>Master failed validation — not saved.</strong><ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul></div>
            </div>
          )}
          <div className="ts-editor-actions">
            <button className="ts-btn ghost sm" onClick={() => setEditing(null)}>Cancel</button>
            <button className="ts-btn ghost sm" onClick={validateLocal} data-testid="styler-validate-json"><CheckCircle2 size={14} /> Validate</button>
            <button className="ts-btn ghost sm" onClick={() => save(false)} disabled={saving} data-testid="styler-save-version">Save as new version</button>
            <button className="ts-btn primary sm" onClick={() => save(true)} disabled={saving} data-testid="styler-save-activate">{saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save & make active</button>
          </div>
        </div>
      )}

      {viewing && (
        <div className="ts-editor" data-testid="styler-version-viewer">
          <div className="ts-editor-head">
            <h4>{viewing.name} — v{viewing.version} {viewing.is_active && <span className="ts-pill">active</span>}</h4>
            <button className="ts-btn ghost sm" onClick={() => setViewing(null)}>Close</button>
          </div>
          <pre className="ts-json"><code>{JSON.stringify(viewing.json, null, 2)}</code></pre>
        </div>
      )}
    </section>
  );
}
